import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { getSceneTranscriptTokens } from './transcript-partition';

import type { MediaFolder, MediaOptions } from 'js/src/client';
import type { RootState } from 'js/src/libs/RootState';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { MusicRecommendationModeEnum, WorkspaceMediaModeEnum } from 'js/src/client';
import { ApiService } from 'js/src/client/services.gen';

type FolderSummary = Pick<MediaFolder, 'name' | 'media_count'>;

/**
 * Gets scene text from text assets as fallback when transcript is empty
 */
export function getSceneTextFallback(scene: Scene): string {
    const textAssets = scene
        .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
        .filter((asset) => asset.isEditable);
    return textAssets
        .map((asset) => asset.text || '')
        .join(' ')
        .trim();
}

/**
 * Gets scene transcript with fallback to text assets
 */
export function getSceneText(state: RootState, scene: Scene): string {
    const transcript = (getSceneTranscriptTokens(state, scene) ?? [])
        .filter((token) => !token.isPause)
        .map((token) => token.word)
        .join(' ')
        .trim();
    return transcript || getSceneTextFallback(scene);
}

export function describeFolders(folders: FolderSummary[]): string {
    return folders.map((folder) => `"${folder.name}" (${folder.media_count} items)`).join(', ');
}

export async function fetchUploadFolders(): Promise<MediaFolder[]> {
    const response = await ApiService.mediaFoldersList();
    return response.data;
}

/**
 * The upload folders the user limited workspace media to, or undefined when uploads
 * aren't limited to folders or the folder list couldn't be loaded.
 */
export async function fetchSelectedUploadFolders(
    mediaOptions: MediaOptions,
): Promise<MediaFolder[] | undefined> {
    if (mediaOptions.workspace_media_mode !== WorkspaceMediaModeEnum.FOLDERS) {
        return undefined;
    }
    try {
        const folders = await fetchUploadFolders();
        return folders.filter((folder) => mediaOptions.workspace_folder_ids.includes(folder.id));
    } catch {
        return undefined;
    }
}

interface MediaPreferencesFormatOptions {
    musicMode?: MusicRecommendationModeEnum;
    selectedFolders?: FolderSummary[];
}

/**
 * Formats media recommendation preferences for LLM context. Emits the enabled
 * media sources (naming the upload folders when `selectedFolders` is supplied), the
 * user's media guidance (verbatim), and — when `musicMode` is supplied — the music
 * preference. Sources that are off are omitted from the list, so the agent treats them
 * as unavailable — except previous uploads, which the preference only keeps out of
 * automatic suggestions, not out of explicit user requests. Callers are
 * responsible for resolving which guidance string to pass (e.g. falling back to
 * workspace guidance); this function renders whatever `media_guidance` it is given.
 */
export function formatMediaPreferencesForLLM(
    mediaOptions: MediaOptions,
    { musicMode, selectedFolders }: MediaPreferencesFormatOptions = {},
): string {
    const lines: string[] = [];

    // Media sources
    const enabledSources: string[] = [];
    if (mediaOptions.stock_media_enabled) enabledSources.push('Stock media');
    if (mediaOptions.stock_icons_enabled) enabledSources.push('Stock icons');
    if (mediaOptions.dam_media_enabled) enabledSources.push('DAM media (see available media)');
    const autoSuggestUploads = mediaOptions.workspace_media_mode !== WorkspaceMediaModeEnum.OFF;
    if (autoSuggestUploads) {
        // Uploads aren't in the available media until searched, so name the search explicitly.
        const folderLimit =
            mediaOptions.workspace_media_mode === WorkspaceMediaModeEnum.FOLDERS
                ? selectedFolders?.length
                    ? `, limited to these folders the user picked: ${describeFolders(selectedFolders)}`
                    : ', limited to the folders the user picked'
                : '';
        enabledSources.push(
            `Previous uploads (find them with ${TOOL_NAMES.searchMedia} with source "uploads"${folderLimit})`,
        );
    }

    if (enabledSources.length > 0) {
        lines.push(`**Enabled media sources**: ${enabledSources.join(', ')}`);
    } else {
        lines.push('**Enabled media sources**: None (working with available media only)');
    }
    if (!autoSuggestUploads) {
        lines.push(
            "**Previous uploads**: not auto-suggested, but use them when the user asks for their own files (e.g. their logo or photos they've uploaded)",
        );
    }

    if (autoSuggestUploads && !mediaOptions.stock_media_enabled) {
        lines.push(
            "**Stock media is off**: the user's uploads are the main source for scene media — search them before falling back to anything else.",
        );
    }

    // Media guidance
    if (mediaOptions.media_guidance && mediaOptions.media_guidance.trim()) {
        lines.push(
            `**Media guidance (try to find media that matches this)**: ${mediaOptions.media_guidance.trim()}`,
        );
    }

    if (musicMode === MusicRecommendationModeEnum.OFF) {
        lines.push('**Music**: off — do not add background music');
    } else if (musicMode === MusicRecommendationModeEnum.STARRED) {
        lines.push("**Music**: use one of the team's starred tracks");
    }

    return lines.join('\n');
}
